import { axiosClient } from './axiosClient';
import { APIUrls } from '../constant/constant';

const { DASHBOARD_ENDPOINTS } = APIUrls;
/**
 * Helper function to handle API errors and throw user-friendly messages
 * @param {Error} error - The original error
 * @param {string} operation - Description of the operation that failed
 * @throws {Error} User-friendly error message
 */
function handleApiError(error, operation) {
  console.error(`Error ${operation}:`, error);
  
  // API server errors (4xx, 5xx responses)
  if (error.response) {
    const status = error.response.status;
    const statusText = error.response.statusText;
    
    if (status >= 400 && status < 500) {
      switch (status) {
        case 400:
          throw new Error(`Bad request while ${operation}. Please check your request and try again.`);
        case 401:
          throw new Error(`Authentication failed while ${operation}. Please log in again.`);
        case 403:
          throw new Error(`Access denied while ${operation}. You don't have permission to access this data.`);
        case 404:
          throw new Error(`Data not found while ${operation}. The requested resource may not exist.`);
        case 429:
          throw new Error(`Too many requests while ${operation}. Please wait a moment and try again.`);
        default:
          throw new Error(`Client error (${status}) while ${operation}. ${statusText || 'Please try again.'}`);
      }
    } else if (status >= 500) {
      switch (status) {
        case 500:
          throw new Error(`Server error while ${operation}. The server encountered an internal error. Please try again later.`);
        case 502:
          throw new Error(`Bad gateway while ${operation}. The server is temporarily unavailable. Please try again later.`);
        case 503:
          throw new Error(`Service unavailable while ${operation}. The server is temporarily overloaded. Please try again later.`);
        case 504:
          throw new Error(`Gateway timeout while ${operation}. The server took too long to respond. Please try again.`);
        default:
          throw new Error(`Server error (${status}) while ${operation}. ${statusText || 'Please try again later.'}`);
      }
    }
  }
  // Network failures or other errors
  else if (error.request) {
    throw new Error(`Network error while ${operation}. Please check your internet connection and try again.`);
  }
  // Other JavaScript errors
  else {
    throw new Error(`Unexpected error while ${operation}. ${error.message || 'Please try again.'}`);
  }
}

/**
 * Build query string from parameters
 * @param {Object} queryParams - Query parameters object
 * @returns {string} Query string
 */
function buildQueryString(queryParams = {}) {
  const params = new URLSearchParams();
  Object.keys(queryParams).forEach(key => {
    if (queryParams[key] !== undefined && queryParams[key] !== null) {
      params.append(key, queryParams[key]);
    }
  });
  return params.toString();
}

/**
 * Build URL with optional query parameters
 * @param {string} baseUrl - Base URL
 * @param {string} plugId - Optional plug ID to append
 * @param {Object} queryParams - Optional query parameters
 * @returns {string} Complete URL
 */
function buildApiUrl(baseUrl, plugId = null, queryParams = {}) {
  let url = plugId ? `${baseUrl}/${plugId}` : baseUrl;
  const queryString = buildQueryString(queryParams);
  return queryString ? `${url}?${queryString}` : url;
}

/**
 * Generic response validation
 * @param {any} data - Response data to validate
 * @param {string} dataType - Type of data being validated (for error messages)
 * @throws {Error} If data is invalid
 */
function validateResponse(data, dataType) {
  if (!data || typeof data !== 'object') {
    throw new Error(`Data parsing error while loading ${dataType} data. The server returned invalid data. Please try again or contact support.`);
  }
}

/**
 * Handle API errors with validation error passthrough
 * @param {Error} error - The error to handle
 * @param {string} operation - Description of the operation
 * @throws {Error} User-friendly error message
 */
function handleApiErrorWithValidation(error, operation) {
  if (error.message.includes('Data parsing error')) {
    throw error; // Re-throw validation errors as-is
  }
  handleApiError(error, operation);
}

/**
 * Handle specific "No data found" errors from 500 responses
 * @param {Error} error - The error to check
 * @param {string} dataType - Type of data (logs, alerts, devices)
 * @param {string} identifier - Device/entity identifier
 * @returns {boolean} True if handled, false otherwise
 */
function handleNoDataFoundError(error, dataType, identifier = '') {
  if (error.response?.status === 500) {
    const errorData = error.response.data;
    if (errorData?.details?.includes(`No ${dataType} found`)) {
      const message = identifier 
        ? `No ${dataType} found for ${identifier}.`
        : `No ${dataType} found for selected criteria.`;
      throw new Error(message);
    }
  }
  return false;
}

/**
 * Validate sites response data
 * @param {any} data - Response data to validate
 * @throws {Error} If data is invalid
 */
function validateSitesResponse(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Data parsing error while loading sites data. The server returned invalid data. Please try again or contact support.');
  }
  
  if (!data.AllSites || !Array.isArray(data.AllSites)) {
    throw new Error('Data parsing error while loading sites data. Invalid response format: AllSites should be an array.');
  }
}

/**
 * Validate devices response data
 * @param {any} data - Response data to validate
 * @throws {Error} If data is invalid
 */
function validateDevicesResponse(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Data parsing error while loading devices data. The server returned invalid data. Please try again or contact support.');
  }
  
  if (!data.devices || !Array.isArray(data.devices)) {
    throw new Error('Data parsing error while loading devices data. Invalid response format: devices should be an array.');
  }
  
  // Validate each device object has required fields
  const requiredFields = ['store_plug_id', 'store_id', 'location_id', 'status'];
  data.devices.forEach((device, index) => {
    requiredFields.forEach(field => {
      if (!device.hasOwnProperty(field)) {
        throw new Error(`Data parsing error while loading devices data. Invalid device data at index ${index}: Missing required field '${field}'.`);
      }
    });
  });
}

/**
 * Validate device details response data
 * @param {any} data - Response data to validate
 * @throws {Error} If data is invalid
 */
function validateDeviceDetailsResponse(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Data parsing error while loading device details. The server returned invalid data. Please try again or contact support.');
  }
  
  if (!data.store_plug_id && !data.id) {
    throw new Error('Data parsing error while loading device details. Invalid device details: Missing device identifier.');
  }
}

/**
 * Get all sites with their locations
 * @returns {Promise<Object>} Sites data with locations
 * @throws {Error} User-friendly error message if API fails
 */
export async function getSitesApi() {
  try {
    const response = await axiosClient.get(DASHBOARD_ENDPOINTS.GET_SITES);
    validateSitesResponse(response.data);
    return response.data;
  } catch (error) {
    handleApiErrorWithValidation(error, 'loading sites data');
  }
}

/**
 * Get ShellyPlug devices with optional filters
 * @param {Object} params - Filter parameters
 * @param {string} params.store_id - Store ID to filter devices (optional)
 * @param {string} params.location_id - Location ID to filter devices (optional)
 * @returns {Promise<Object>} Devices data
 * @throws {Error} User-friendly error message if API fails
 */
export async function getDevicesApi(params = {}) {
  try {
    // Prepare query parameters
    const queryParams = { ...params };
    
    // If no query parameters are provided, add all_sites=true
    if (!params.store_id && !params.location_id) {
      queryParams.all_sites = 'true';
    }
    
    const url = buildApiUrl(DASHBOARD_ENDPOINTS.GET_DEVICES, null, queryParams);
    
    const response = await axiosClient.get(url);
    validateDevicesResponse(response.data);
    return response.data;
  } catch (error) {
    handleNoDataFoundError(error, 'devices');
    handleApiErrorWithValidation(error, 'loading devices data');
  }
}

/**
 * Get device details by plug ID
 * @param {string} plugId - The plug ID to fetch details for
 * @returns {Promise<Object>} Device details
 * @throws {Error} User-friendly error message if API fails
 */
export async function getDeviceByIdApi(plugId) {
  try {
    const url = buildApiUrl(DASHBOARD_ENDPOINTS.GET_DEVICE_BY_ID, plugId);
    const response = await axiosClient.get(url);
    validateDeviceDetailsResponse(response.data);
    return response.data;
  } catch (error) {
    handleApiErrorWithValidation(error, `loading details for device ${plugId}`);
  }
}

/**
 * Get logs for a specific plug ID with optional query parameters
 * @param {string} plugId - The plug ID to fetch logs for (e.g., 'plug300')
 * @param {Object} queryParams - Optional query parameters for filtering
 * @param {string} queryParams.startdate - Start date for filtering (YYYY-MM-DDTHH:MM format)
 * @param {string} queryParams.enddate - End date for filtering (YYYY-MM-DDTHH:MM format)
 * @param {number} queryParams.page - Page number for pagination
 * @param {number} queryParams.page_size - Number of items per page
 * @param {string} queryParams.sort_by - Field to sort by
 * @param {string} queryParams.sort_order - Sort order (asc/desc)
 * @returns {Promise<Object>} Logs data
 * @throws {Error} User-friendly error message if API fails
 */
export async function getLogsApi(plugId, queryParams = {}) {
  try {
    const url = buildApiUrl(DASHBOARD_ENDPOINTS.GET_LOGS, plugId, queryParams);
    const response = await axiosClient.get(url);
    
    validateResponse(response.data, 'logs');
    return response.data;
  } catch (error) {
    handleNoDataFoundError(error, 'logs', `device ${plugId}`);
    handleApiErrorWithValidation(error, `loading logs for device ${plugId}`);
  }
}



/**
 * Get alerts for a specific plug ID with optional query parameters
 * @param {string} plugId - The plug ID to fetch alerts for (e.g., 'plug300')
 * @param {Object} queryParams - Optional query parameters for filtering
 * @param {string} queryParams.startdate - Start date for filtering (YYYY-MM-DDTHH:MM format)
 * @param {string} queryParams.enddate - End date for filtering (YYYY-MM-DDTHH:MM format)
 * @param {number} queryParams.page - Page number for pagination
 * @param {number} queryParams.page_size - Number of items per page
 * @param {string} queryParams.sort_by - Field to sort by
 * @param {string} queryParams.sort_order - Sort order (asc/desc)
 * @returns {Promise<Object>} Alerts data
 * @throws {Error} User-friendly error message if API fails
 */
export async function getAlertsApi(plugId, queryParams = {}) {
  try {
    const url = buildApiUrl(DASHBOARD_ENDPOINTS.GET_ALERTS, plugId, queryParams);
    const response = await axiosClient.get(url);
    
    validateResponse(response.data, 'alerts');
    return response.data;
  } catch (error) {
    // Handle 500 errors with specific message for plug details
    if (error.response?.status === 500) {
      throw new Error(`Unable to check the device alerts, please try again.`);
    }
    
    handleNoDataFoundError(error, 'alerts', `device ${plugId}`);
    handleApiErrorWithValidation(error, `loading alerts for device ${plugId}`);
  }
}

/**
 * Get devices for specific location
 * @param {string} locationId - Location ID to filter devices
 * @returns {Promise<Object>} Devices data for the location
 */
export async function getDevicesForLocationApi(locationId) {
  return getDevicesApi({ location_id: locationId });
}

/**
 * Get devices for specific store
 * @param {string} storeId - Store ID to filter devices
 * @returns {Promise<Object>} Devices data for the store
 */
export async function getDevicesForStoreApi(storeId) {
  return getDevicesApi({ store_id: storeId });
}


